/**
 * Onboarding Storage Utilities
 *
 * Centralized localStorage helpers for onboarding state.
 * Used by OnboardingPanel, OnboardingSuccessBanner, and AddTradeModal.
 */

// ============================================================================
// Storage Keys
// ============================================================================

const ONBOARDING_DISMISSED_KEY = 'optioneer_onboarding_dismissed'
const ONBOARDING_VISITED_KEY = 'optioneer_onboarding_visited'
const JUST_ADDED_FIRST_TRADE_KEY = 'optioneer_just_added_first_trade'
const PORTFOLIO_BANNER_DISMISSED_KEY = 'optioneer_portfolio_banner_dismissed'
const JUST_ADDED_FIRST_STOCK_KEY = 'optioneer_just_added_first_stock'
const ACTIVE_USER_KEY = 'optioneer_onboarding_active_user_id'

export const ONBOARDING_STEP_VISITED_EVENT = 'optioneer:onboarding-step-visited'

export interface OnboardingStepVisitedDetail {
  stepId: string
  userId: string
}

const SCOPED_KEYS = [
  ONBOARDING_DISMISSED_KEY,
  ONBOARDING_VISITED_KEY,
  JUST_ADDED_FIRST_TRADE_KEY,
  JUST_ADDED_FIRST_STOCK_KEY,
  PORTFOLIO_BANNER_DISMISSED_KEY,
]

let activeUserId: string | null = null

function normalizeUserId(userId?: string | null): string {
  const explicitUserId = userId?.trim()
  if (explicitUserId) return explicitUserId
  if (activeUserId) return activeUserId

  try {
    const storedUserId = localStorage.getItem(ACTIVE_USER_KEY)?.trim()
    if (storedUserId) return storedUserId
  } catch {
    // Ignore storage access errors
  }

  return 'anonymous'
}

export function getOnboardingStorageUserId(userId?: string | null): string {
  return normalizeUserId(userId)
}

function scopedKey(key: string, userId?: string | null): string {
  return `${key}:${normalizeUserId(userId)}`
}

/**
 * Set the active storage user used by callers that do not pass an explicit user id.
 */
export function setOnboardingStorageUserId(userId?: string | null): void {
  activeUserId = userId?.trim() || null

  try {
    if (activeUserId) {
      localStorage.setItem(ACTIVE_USER_KEY, activeUserId)
    } else {
      localStorage.removeItem(ACTIVE_USER_KEY)
    }
  } catch {
    // Ignore storage access errors
  }
}

/**
 * Clear onboarding state for a user. Also removes legacy unscoped keys so old
 * browser/mobile WebView state cannot leak into a fresh onboarding run.
 */
export function clearOnboardingState(userId?: string | null): void {
  const normalizedUserId = normalizeUserId(userId)

  SCOPED_KEYS.forEach((key) => {
    localStorage.removeItem(`${key}:${normalizedUserId}`)
    localStorage.removeItem(key)
  })
}

// ============================================================================
// Visited Steps (for step completion tracking)
// ============================================================================

/**
 * Get the set of visited onboarding steps
 */
export function getVisitedSteps(userId?: string | null): Set<string> {
  try {
    const saved = localStorage.getItem(scopedKey(ONBOARDING_VISITED_KEY, userId))
    if (saved) {
      return new Set(JSON.parse(saved) as string[])
    }
  } catch {
    // Ignore parse errors
  }
  return new Set()
}

export function setVisitedSteps(stepIds: Iterable<string>, userId?: string | null): void {
  localStorage.setItem(
    scopedKey(ONBOARDING_VISITED_KEY, userId),
    JSON.stringify(Array.from(new Set(stepIds)))
  )
}

/**
 * Mark an onboarding step as visited/completed
 */
export function markStepVisited(stepId: string, userId?: string | null): void {
  const normalizedUserId = normalizeUserId(userId)
  const visited = getVisitedSteps(userId)
  const wasAlreadyVisited = visited.has(stepId)
  visited.add(stepId)
  setVisitedSteps(visited, normalizedUserId)

  if (!wasAlreadyVisited && typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent<OnboardingStepVisitedDetail>(ONBOARDING_STEP_VISITED_EVENT, {
        detail: { stepId, userId: normalizedUserId },
      })
    )
  }
}

/**
 * Check if a specific step has been visited
 */
export function hasVisitedStep(stepId: string, userId?: string | null): boolean {
  return getVisitedSteps(userId).has(stepId)
}

// ============================================================================
// Onboarding Panel Dismiss State
// ============================================================================

/**
 * Check if the onboarding panel was dismissed
 */
export function isOnboardingDismissed(userId?: string | null): boolean {
  return localStorage.getItem(scopedKey(ONBOARDING_DISMISSED_KEY, userId)) === 'true'
}

/**
 * Dismiss the onboarding panel
 */
export function dismissOnboarding(userId?: string | null): void {
  localStorage.setItem(scopedKey(ONBOARDING_DISMISSED_KEY, userId), 'true')
}

/**
 * Reopen the onboarding panel (clear dismissed state)
 */
export function reopenOnboarding(userId?: string | null): void {
  localStorage.removeItem(scopedKey(ONBOARDING_DISMISSED_KEY, userId))
}

// ============================================================================
// First Trade Flag (one-time trigger for portfolio banner)
// ============================================================================

/**
 * Set the "just added first trade" flag
 * Called by AddTradeModal when user adds their first trade
 */
export function setJustAddedFirstTrade(userId?: string | null): void {
  localStorage.setItem(scopedKey(JUST_ADDED_FIRST_TRADE_KEY, userId), '1')
}

/**
 * Check if the "just added first trade" flag is set
 */
export function hasJustAddedFirstTrade(userId?: string | null): boolean {
  return localStorage.getItem(scopedKey(JUST_ADDED_FIRST_TRADE_KEY, userId)) === '1'
}

/**
 * Clear the "just added first trade" flag
 * Called after the portfolio banner is shown
 */
export function clearJustAddedFirstTrade(userId?: string | null): void {
  localStorage.removeItem(scopedKey(JUST_ADDED_FIRST_TRADE_KEY, userId))
}

// ============================================================================
// Portfolio Banner Dismiss State
// ============================================================================

/**
 * Check if the portfolio onboarding banner was dismissed
 */
export function isPortfolioBannerDismissed(userId?: string | null): boolean {
  return localStorage.getItem(scopedKey(PORTFOLIO_BANNER_DISMISSED_KEY, userId)) === 'true'
}

/**
 * Dismiss the portfolio onboarding banner
 */
export function dismissPortfolioBanner(userId?: string | null): void {
  localStorage.setItem(scopedKey(PORTFOLIO_BANNER_DISMISSED_KEY, userId), 'true')
}

// ============================================================================
// First Stock Flag (one-time trigger for stock success banner)
// ============================================================================

/**
 * Set the "just added first stock" flag
 * Called by AddStockModal when user adds their first stock position
 */
export function setJustAddedFirstStock(userId?: string | null): void {
  localStorage.setItem(scopedKey(JUST_ADDED_FIRST_STOCK_KEY, userId), '1')
}

/**
 * Check if the "just added first stock" flag is set
 */
export function hasJustAddedFirstStock(userId?: string | null): boolean {
  return localStorage.getItem(scopedKey(JUST_ADDED_FIRST_STOCK_KEY, userId)) === '1'
}

/**
 * Clear the "just added first stock" flag
 * Called after the stock success banner is shown
 */
export function clearJustAddedFirstStock(userId?: string | null): void {
  localStorage.removeItem(scopedKey(JUST_ADDED_FIRST_STOCK_KEY, userId))
}
